import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { projectURL } from "./utils/mockdata";

const TopNavBar = () => {
  const navigate = useNavigate();
  const [personName, setPersonName] = useState(null);
  const [personImage, setPersonImage] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const loggedUserId = sessionStorage.getItem("loggedUserId");

  useEffect(() => {
    const loggedInUser = sessionStorage.getItem("authenticated");
    if (loggedInUser === "true") {
      setPersonName(sessionStorage.getItem("loggedUserName"));
      setPersonImage(sessionStorage.getItem("loggedInUserImage"));
      fetchNotifications();

      // 🔁 Auto-refresh 
      const interval = setInterval(() => {
        fetchNotifications();
      }, 3000); // 3 seconds

      // 🧹 Cleanup interval when component unmounts
      return () => clearInterval(interval);

    }
  }, []);

  // 🔄 Fetch all notifications
  const fetchNotifications = async () => {
    try {
      const loggedUserId = sessionStorage.getItem("loggedUserId");
      if (!loggedUserId) return;

      const apiUrl = `${projectURL}notification/list?loggedUserId=${loggedUserId}&limit=15&page=1`;
      const response = await fetch(apiUrl, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      const data = await response.json();
      const formatted = data.notifications.map((n) => ({
        id: n._id,
        text: n.message,
        read: n.flag === "read",
        createdAt: new Date(n.created_at).toLocaleString(),
      }));

      setNotifications(formatted);
    } catch (error) {
      console.error("❌ Error fetching notifications:", error);
    }
  };

  // 🟢 Mark a notification as read (API + UI)
  const markNotificationAsRead = async (id) => {
    try {
      // Update backend
      const apiUrl = `${projectURL}notification/${id}`;
      const response = await fetch(apiUrl, {
        method: "PUT",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flag: "read" }),
      });

      if (!response.ok) {
        console.error("Failed to update notification status:", response.status);
        return;
      }

      // Update UI instantly
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error("❌ Error updating notification:", error);
    }
  };


  const handleLogout = (e) => {
    e.preventDefault();
    sessionStorage.clear();
    navigate("/");
  };

  const handleProfile = () => navigate("/profile");
  const handleHome = () => navigate("/dashboard");
  const handleStat = () => navigate("/statistics");
  const handleGamification = () => navigate("/gamification");
  const handleFindFriends = () => navigate("/findfriends");
  const handleUpcomingBday = () => navigate("/upcomingbday");
  const handleLoginHistory = () => navigate("/loginhistory");
  const handleChat = () => navigate("/chat");
  const handleQueries = () => navigate('/ViewQueries');

  return (
    <nav className="bg-blue-900 sticky top-0 z-50">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-4">
        <div className="flex h-20 items-center justify-between">
          <div className="flex items-center">
            <span
              style={{ cursor: "pointer" }}
              onClick={handleHome}
              className="shrink-0"
            >
              <img
                className="ring-2 ring-white max-w-10 border rounded-full p-1.5"
                src="icon.png"
                alt="Socio"
              />
            </span>
          </div>

          <div className="flex items-center">
            {/* 🔔 Notifications */}
            <div className="relative ml-3">
              <button
                type="button"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative rounded-full p-1 text-gray-400 hover:text-white focus:outline-none ring-2 ring-white"
              >
                <svg
                  className="size-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
                  />
                </svg>
                {notifications.filter((n) => !n.read).length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full">
                    {notifications.filter((n) => !n.read).length}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 z-10 mt-2 w-80 max-h-96 overflow-y-auto rounded-md bg-white py-2 shadow-lg ring-1 ring-black/5">
                  {notifications.length === 0 ? (
                    <p className="text-gray-500 text-sm px-4 py-2">
                      No notifications
                    </p>
                  ) : (
                    notifications.map((notification) => (
                      <div key={notification.id}>
                        <p
                          className={`text-sm px-4 py-2 hover:bg-gray-100 cursor-pointer ${notification.read
                            ? "text-gray-400"
                            : "text-gray-700 font-medium"
                            }`}
                          onClick={() => markNotificationAsRead(notification.id)}
                        >
                          {notification.text}
                          <br />
                          <span className="text-xs text-gray-400">
                            {notification.createdAt}
                          </span>
                        </p>
                        <hr className="border-gray-200" />
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* 👤 Profile Menu */}
            <div className="relative ml-3">
              <div onClick={() => setShowProfileMenu(!showProfileMenu)}>
                <button
                  type="button"
                  className="relative flex max-w-xs items-center rounded-full bg-blue-100 text-sm focus:outline-none ring-2 ring-white"
                >
                  <img
                    className="size-8 rounded-full"
                    src={personImage}
                    alt=""
                  />
                </button>
              </div>

              {showProfileMenu && (
                <div className="absolute right-0 z-10 mt-2 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5">
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={handleProfile}
                    className="block px-4 py-2 text-sm text-gray-700 flex"
                  >
                    <img
                      className="size-5 rounded-full"
                      src={personImage}
                      alt=""
                    />
                    <span className="ml-2 text-blue-600">{personName}</span>
                  </span>
                  <hr className="border-gray-200" />
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={handleHome}
                    className="block px-4 py-2 text-sm text-gray-700"
                  >
                    Home
                  </span>
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={handleFindFriends}
                    className="block px-4 py-2 text-sm text-gray-700"
                  >
                    Find Friends
                  </span>
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={handleUpcomingBday}
                    className="block px-4 py-2 text-sm text-gray-700"
                  >
                    Upcoming Bday
                  </span>
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={handleLoginHistory}
                    className="block px-4 py-2 text-sm text-gray-700">
                    My Login History
                  </span>
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={handleStat}
                    className="block px-4 py-2 text-sm text-gray-700"
                  >
                    Statistics
                  </span>
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={handleGamification}
                    className="block px-4 py-2 text-sm text-gray-700"
                  >
                    Gamification
                  </span>
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={handleChat}
                    className="block px-4 py-2 text-sm text-gray-700"
                  >
                    Chat
                  </span>
                  {loggedUserId === '682869c8f03aafcb8b90e2b5' ? (<span
                    style={{ cursor: "pointer" }}
                    onClick={handleQueries}
                    className="block px-4 py-2 text-sm text-gray-700"
                  >
                    View Queries
                  </span>) : ''}

                  <hr className="border-gray-200" />
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopNavBar;
